import type { WarehouseAddress } from '@befret/core/reference';

/**
 * L'adresse de l'entrepôt telle qu'une page publique l'affiche — trois lignes
 * composées et le pays dans la langue du visiteur.
 */
export interface WarehouseAddressView {
  company: string;
  street: string;
  locality: string;
  country: string;
}

/**
 * Le strict nécessaire du service de traduction pour rendre un nom de pays.
 * Le déclarer ainsi garde cet utilitaire testable sans TestBed et sans dépendre
 * de toute la surface du service.
 */
export interface CountryNameTranslator {
  hasTranslation(key: string): boolean;
  instant(key: string, params?: unknown): string;
}

/**
 * #1561 / #1570 — la mise en forme d'affichage de `reference_data/warehouse_address`.
 *
 * Deux pages publiques montrent la même adresse : la carte « votre adresse
 * belge » de l'accueil et la maquette d'adresse de `vm/info`. Toutes deux
 * affichaient une adresse INVENTÉE écrite dans les fichiers de langue — le
 * néerlandais avait même traduit la fausse rue, preuve qu'un mock rangé dans un
 * catalogue i18n finit par se lire comme du contenu.
 *
 * L'adresse a une source unique et administrable ; sa mise en forme vit ici,
 * une seule fois, pour que les deux pages ne puissent pas diverger — c'est
 * exactement ce qui s'était produit avec les deux textes de garde (#1563) et
 * les cinq délais de livraison (#1569).
 *
 * ⚠️ Ce que cette fonction ne rend PAS : le nom du destinataire et son code
 * BeFret. Ils ne sont pas des données de l'entrepôt, et publier un code
 * plausible à côté d'une VRAIE adresse ferait partir des colis réels sous un
 * code qui n'appartient à personne (file `vm-unidentified-packages`). Les pages
 * n'en publient qu'un gabarit, porté par leur propre clé de traduction.
 *
 * Le nom du pays est le seul élément traduisible : le référentiel n'en porte
 * que le code ISO. `hasTranslation()` est le SEUL test fiable de présence
 * (#1403 — `instant()` rend un repli d'affichage sur un miss, jamais la clé) ;
 * sans traduction, on publie le code plutôt qu'une clé nue.
 */
export function warehouseAddressView(
  warehouse: WarehouseAddress,
  translation: CountryNameTranslator,
): WarehouseAddressView {
  const countryKey = `COUNTRIES.${warehouse.country}`;

  return {
    company: warehouse.company,
    street: [warehouse.street, warehouse.houseNumber].filter(Boolean).join(' '),
    locality: [warehouse.postalCode, warehouse.city].filter(Boolean).join(' '),
    country: translation.hasTranslation(countryKey)
      ? translation.instant(countryKey)
      : warehouse.country,
  };
}
